import { ASSIGNEE_TYPES } from "../constant/assigneeTypes";
import moment from "moment-timezone";
import { capitalizeFirstLetter } from "./string";
import _ from "lodash";

export const formatAssignee = (assignee) => {
  switch (assignee.type) {
    case ASSIGNEE_TYPES.user:
      return `${assignee.lastName}, ${assignee.firstName}`;
    case ASSIGNEE_TYPES.group:
      return assignee.name;
    default:
      return `${assignee.lastName}, ${assignee.firstName}`;
  }
};

export const formatAssigneesToDisplay = (assignees = []) => {
  return assignees.length
    ? assignees
        .map((assignee) =>
          assignee.type === "user-group" ? assignee.name : `${assignee.lastName}, ${assignee.firstName}`
        )
        .join(" | ")
    : "";
};

export const formatAssigneesToExport = (assignees = []) => {
  return assignees.length
    ? assignees
        .map((assignee) =>
          assignee.type === "user-group" ? assignee.name : `${assignee.firstName} ${assignee.lastName}`
        )
        .join(" , ")
    : "";
};

export const formatPhoneNumberToDisplay = (value) => {
  if (!value) return value;

  const phoneNumber = value.replace(/[^\d]/g, "");

  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;

  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

export const formatSSNToDisplay = (value) => {
  if (!value) return value;

  const ssn = value.replace(/[^\d]/g, "");
  const ssnLength = ssn.length;

  if (ssnLength < 4) return ssn;

  if (ssnLength < 6) {
    return `XXX-${ssn.slice(3)}`;
  }

  return `XXX-XX-${ssn.slice(5)}`;
};

export const decodeDate = (date) => {
  if (!date) return date;

  const momentDate = moment(date);
  if (!momentDate.isValid()) {
    return "";
  }

  return momentDate.format("MM/DD/YYYY");
};

function buildFormData(formData, data, parentKey) {
  if (data && typeof data === "object" && !(data instanceof Date) && !(data instanceof File)) {
    Object.keys(data).forEach((key) => {
      buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
    });
  } else {
    const value = data == null ? "" : data;

    formData.append(parentKey, value);
  }
}

export function jsonToFormData(data) {
  const formData = new FormData();

  buildFormData(formData, data);
  const clonedData = [...formData.entries()];
  const urlEncodedData = clonedData
    .map((entry) => `${encodeURIComponent(entry[0])}=${encodeURIComponent(entry[1])}`)
    .join("&");
  return urlEncodedData;
}
export const decodeNumbersForSearch = (phoneNumber) => {
  if (!phoneNumber) return phoneNumber;

  return phoneNumber.replace(/[^a-zA-Z0-9]/g, "");
};

export const formatPhoneNumber = (phoneNumberString) => {
  const cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  const match = cleaned.match(/^(1)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return cleaned;
};

export const formatDrugName = (drugName, drugDosage, drugUnit) => {
  if (!drugName) {
    return "";
  } else if (!drugDosage || !drugUnit) {
    return capitalizeFirstLetter(drugName);
  } else {
    return capitalizeFirstLetter(`${drugName} ${drugDosage}${drugUnit}`);
  }
};

export const decimalRegex = /^\d*\.?\d*$/;
export const integerRegex = /^\d+$/;

export const limitDecimalInputPrecision = (e, numberOfAllowedDecimals) => {
  if (!e.target?.value || !e.key || isNaN(e.target?.value)) {
    return;
  }

  const charCode = e.key;
  const isKeyNumber = charCode >= "0" && charCode <= "9";
  const allowedDecimalRegex = new RegExp(`[.][0-9]{${numberOfAllowedDecimals},}`);
  const inputReachedPrecisionLimit = allowedDecimalRegex.test(e.target.value);

  if (isKeyNumber && inputReachedPrecisionLimit) {
    const cursorPosition = e.target.selectionStart;
    const dotPosition = e.target.value.indexOf(".");

    if (cursorPosition > dotPosition) {
      e.preventDefault();
    }
  }
  return;
};

export function numberWithCommas(value) {
  return !value || isNaN(value) ? "" : value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const formatToDecimal = (value) => {
  if (!value) return "";

  const numberValue = Number(value);
  if (numberValue % 1 === 0) {
    return numberWithCommas(numberValue);
  }
  return numberWithCommas(numberValue.toFixed(2));
};

export const formatDosage = (drugDosage, dosageUnit) => {
  if (drugDosage && dosageUnit) {
    return `${drugDosage} ${dosageUnit}`;
  }
  return "";
};
export const parseDecimals = (number, precision = 2) => {
  if (!number || !parseFloat(number)) return "";

  number = parseFloat(number).toString();

  const [integerPart, decimalPart] = number.split(".");

  let result = integerPart || "";

  if (decimalPart) {
    result += "." + decimalPart.slice(0, precision);
  }
  return parseFloat(result);
};

export const formatAssigneeGroupName = (assignees) => {
  if (!assignees || !assignees.length) return;
  const userGroups = _.uniq(
    assignees.flatMap((assignee) =>
      assignee.type === "user-group"
        ? assignee.name
        : assignee.userGroup?.trim().length
        ? assignee.userGroup.split(",")
        : []
    )
  );
  const assigneeGroupNames = userGroups.join(", ");
  return assigneeGroupNames;
};

export const cleanupAndFormatStrings = (...args) => {
  return args.filter(Boolean).join(" ");
};

export const stripHtml = (text) => {
  return text.replace(/<[^>]*>/g, "");
};
